import React, { useRef, useEffect, useState } from 'react';
import * as d3 from 'd3';

const MARGIN = { top: 20, right: 20, bottom: 50, left: 60 };
const HEIGHT = 480;

const FIELD_LABELS = {
  pest365: 'Pest365',
  pest365Plus: 'Pest365+',
  oneTime: 'One-Time',
  initialServiceVisit: 'Init. Visit',
  mosquitoTickControl: 'Mosquito/Tick',
};

const ARROW_COLORS = {
  pest365: '#dc2626',
  pest365Plus: '#ea580c',
  oneTime: '#2563eb',
  initialServiceVisit: '#7c3aed',
  mosquitoTickControl: '#059669',
};

const styles = {
  card: {
    position: 'relative',
    background: '#fff',
    border: '1px solid #e5e7eb',
    borderRadius: '8px',
    padding: '24px',
    fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
  },
  title: {
    fontSize: '16px',
    fontWeight: 600,
    color: '#1f2937',
    marginBottom: '2px',
  },
  subtitle: {
    fontSize: '12px',
    color: '#9ca3af',
    marginBottom: '16px',
  },
  legend: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: '12px',
    marginBottom: '12px',
    alignItems: 'center',
  },
  legendItem: {
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
    fontSize: '12px',
    fontWeight: 500,
    color: '#374151',
  },
  legendDot: {
    width: '10px',
    height: '10px',
    borderRadius: '50%',
    flexShrink: 0,
  },
  container: {
    overflowX: 'auto',
    minWidth: '400px',
  },
  tooltip: {
    position: 'fixed',
    pointerEvents: 'none',
    background: '#fff',
    border: '1px solid #e5e7eb',
    borderRadius: '6px',
    padding: '8px 12px',
    boxShadow: '0 2px 8px rgba(0,0,0,0.12)',
    fontSize: '12px',
    zIndex: 1000,
    fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
    maxWidth: '240px',
  },
  varianceBadge: {
    display: 'inline-flex',
    alignItems: 'center',
    padding: '2px 10px',
    borderRadius: '999px',
    background: '#eff6ff',
    border: '1px solid #bfdbfe',
    color: '#2563eb',
    fontSize: '12px',
    fontWeight: 600,
    whiteSpace: 'nowrap',
  },
  emptyState: {
    color: '#6b7280',
    textAlign: 'center',
    padding: '40px 0',
  },
};

export default function PCABiplot({ data }) {
  const svgRef = useRef(null);
  const containerRef = useRef(null);
  const [tooltip, setTooltip] = useState(null);

  useEffect(() => {
    if (!data || !data.scores || data.scores.length === 0) return;

    const svg = d3.select(svgRef.current);
    svg.selectAll('*').remove();

    const { scores, loadings, varianceExplained } = data;

    const containerWidth = containerRef.current
      ? containerRef.current.clientWidth
      : 700;
    const width = Math.max(400, containerWidth);
    const innerW = width - MARGIN.left - MARGIN.right;
    const innerH = HEIGHT - MARGIN.top - MARGIN.bottom;

    svg.attr('width', width).attr('height', HEIGHT);

    const g = svg.append('g').attr('transform', `translate(${MARGIN.left}, ${MARGIN.top})`);

    // Scales for scores
    const xExtent = d3.extent(scores, (d) => d.pc1);
    const yExtent = d3.extent(scores, (d) => d.pc2);
    const xPad = (xExtent[1] - xExtent[0]) * 0.15 || 1;
    const yPad = (yExtent[1] - yExtent[0]) * 0.15 || 1;

    const x = d3.scaleLinear()
      .domain([xExtent[0] - xPad, xExtent[1] + xPad])
      .nice()
      .range([0, innerW]);

    const y = d3.scaleLinear()
      .domain([yExtent[0] - yPad, yExtent[1] + yPad])
      .nice()
      .range([innerH, 0]);

    // State color scale
    const states = [...new Set(scores.map((d) => d.state))].sort();
    const stateColor = d3.scaleOrdinal(d3.schemeTableau10).domain(states);

    // Grid lines - horizontal
    g.selectAll('.grid-h')
      .data(y.ticks())
      .enter()
      .append('line')
      .attr('class', 'grid-h')
      .attr('x1', 0)
      .attr('x2', innerW)
      .attr('y1', (d) => y(d))
      .attr('y2', (d) => y(d))
      .attr('stroke', '#f3f4f6')
      .attr('stroke-width', 1);

    // Grid lines - vertical
    g.selectAll('.grid-v')
      .data(x.ticks())
      .enter()
      .append('line')
      .attr('class', 'grid-v')
      .attr('x1', (d) => x(d))
      .attr('x2', (d) => x(d))
      .attr('y1', 0)
      .attr('y2', innerH)
      .attr('stroke', '#f3f4f6')
      .attr('stroke-width', 1);

    // Origin crosshair (dashed lines at x=0, y=0)
    if (x.domain()[0] <= 0 && x.domain()[1] >= 0) {
      g.append('line')
        .attr('x1', x(0))
        .attr('x2', x(0))
        .attr('y1', 0)
        .attr('y2', innerH)
        .attr('stroke', '#d1d5db')
        .attr('stroke-width', 1.5)
        .attr('stroke-dasharray', '6,4');
    }

    if (y.domain()[0] <= 0 && y.domain()[1] >= 0) {
      g.append('line')
        .attr('x1', 0)
        .attr('x2', innerW)
        .attr('y1', y(0))
        .attr('y2', y(0))
        .attr('stroke', '#d1d5db')
        .attr('stroke-width', 1.5)
        .attr('stroke-dasharray', '6,4');
    }

    // X axis
    const pc1Label = varianceExplained && varianceExplained[0] != null
      ? `PC1 (${Math.round(varianceExplained[0] * 100)}% variance)`
      : 'PC1';

    const pc2Label = varianceExplained && varianceExplained[1] != null
      ? `PC2 (${Math.round(varianceExplained[1] * 100)}% variance)`
      : 'PC2';

    g.append('g')
      .attr('transform', `translate(0, ${innerH})`)
      .call(
        d3.axisBottom(x)
          .ticks(8)
          .tickSize(0)
      )
      .call((axis) => axis.select('.domain').attr('stroke', '#e5e7eb'))
      .selectAll('text')
      .attr('font-size', '10px')
      .attr('fill', '#6b7280');

    // X axis label
    g.append('text')
      .attr('x', innerW / 2)
      .attr('y', innerH + 40)
      .attr('text-anchor', 'middle')
      .attr('font-size', '11px')
      .attr('fill', '#6b7280')
      .text(pc1Label);

    // Y axis
    g.append('g')
      .call(
        d3.axisLeft(y)
          .ticks(8)
          .tickSize(0)
      )
      .call((axis) => axis.select('.domain').attr('stroke', '#e5e7eb'))
      .selectAll('text')
      .attr('font-size', '10px')
      .attr('fill', '#6b7280');

    // Y axis label
    g.append('text')
      .attr('transform', 'rotate(-90)')
      .attr('x', -innerH / 2)
      .attr('y', -45)
      .attr('text-anchor', 'middle')
      .attr('font-size', '11px')
      .attr('fill', '#6b7280')
      .text(pc2Label);

    // Draw score points (address dots)
    scores.forEach((d) => {
      g.append('circle')
        .attr('cx', x(d.pc1))
        .attr('cy', y(d.pc2))
        .attr('r', 5)
        .attr('fill', stateColor(d.state))
        .attr('opacity', 0.7)
        .attr('stroke', '#fff')
        .attr('stroke-width', 0.5)
        .style('cursor', 'pointer')
        .on('mousemove', (event) => {
          d3.select(event.currentTarget).attr('r', 8).attr('opacity', 1);
          setTooltip({
            x: event.clientX + 12,
            y: event.clientY - 10,
            item: d,
            color: stateColor(d.state),
          });
        })
        .on('mouseleave', (event) => {
          d3.select(event.currentTarget).attr('r', 5).attr('opacity', 0.7);
          setTooltip(null);
        });
    });

    // Draw loading vectors (arrows from origin)
    if (loadings && loadings.length > 0) {
      // Arrowhead marker definition
      const defs = svg.append('defs');

      loadings.forEach((ld) => {
        const color = ARROW_COLORS[ld.field] || '#374151';
        const markerId = `arrow-${ld.field}`;

        defs.append('marker')
          .attr('id', markerId)
          .attr('viewBox', '0 0 10 6')
          .attr('refX', 10)
          .attr('refY', 3)
          .attr('markerWidth', 10)
          .attr('markerHeight', 6)
          .attr('orient', 'auto')
          .append('path')
          .attr('d', 'M0,0 L10,3 L0,6 Z')
          .attr('fill', color);
      });

      // Scale loadings to fit nicely in the plot
      // Use ~40% of the axis range so arrows are visually prominent
      const maxLoading = d3.max(loadings, (ld) => Math.sqrt(ld.pc1 * ld.pc1 + ld.pc2 * ld.pc2)) || 1;
      const arrowScaleX = (innerW * 0.35) / maxLoading;
      const arrowScaleY = (innerH * 0.35) / maxLoading;

      loadings.forEach((ld) => {
        const color = ARROW_COLORS[ld.field] || '#374151';
        const markerId = `arrow-${ld.field}`;
        const label = FIELD_LABELS[ld.field] || ld.field;

        const originX = x(0);
        const originY = y(0);
        const endX = originX + ld.pc1 * arrowScaleX;
        const endY = originY - ld.pc2 * arrowScaleY;

        // Arrow line
        g.append('line')
          .attr('x1', originX)
          .attr('y1', originY)
          .attr('x2', endX)
          .attr('y2', endY)
          .attr('stroke', color)
          .attr('stroke-width', 2)
          .attr('marker-end', `url(#${markerId})`);

        // Label at arrow tip
        const labelOffset = 14;
        const angle = Math.atan2(endY - originY, endX - originX);
        const labelX = endX + Math.cos(angle) * labelOffset;
        const labelY = endY + Math.sin(angle) * labelOffset;

        g.append('text')
          .attr('x', labelX)
          .attr('y', labelY)
          .attr('text-anchor', Math.abs(angle) > Math.PI / 2 ? 'end' : 'start')
          .attr('dominant-baseline', 'central')
          .attr('font-size', '11px')
          .attr('font-weight', 600)
          .attr('fill', color)
          .attr('font-family', "-apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif")
          .text(label);
      });
    }

    return () => {
      svg.selectAll('*').remove();
    };
  }, [data]);

  if (!data || !data.scores || data.scores.length === 0) {
    return (
      <div style={styles.card}>
        <div style={styles.title}>PCA Biplot</div>
        <div style={styles.subtitle}>Principal component analysis of pricing profiles</div>
        <div style={styles.emptyState}>No PCA data available</div>
      </div>
    );
  }

  const { scores, totalVarianceExplained } = data;

  // Build legend from top states
  const stateCounts = {};
  scores.forEach((d) => {
    stateCounts[d.state] = (stateCounts[d.state] || 0) + 1;
  });
  const topStates = Object.entries(stateCounts)
    .sort((a, b) => b[1] - a[1])
    .slice(0, 5)
    .map(([state]) => state);

  const allStates = [...new Set(scores.map((d) => d.state))].sort();
  const stateColor = d3.scaleOrdinal(d3.schemeTableau10).domain(allStates);

  return (
    <div style={styles.card}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '4px' }}>
        <div>
          <div style={styles.title}>PCA Biplot</div>
        </div>
        {totalVarianceExplained != null && (
          <div style={styles.varianceBadge}>
            {Math.round(totalVarianceExplained * 100)}% variance explained
          </div>
        )}
      </div>
      <div style={styles.subtitle}>Principal component analysis of pricing profiles</div>
      <div style={styles.legend}>
        {topStates.map((state) => (
          <div key={state} style={styles.legendItem}>
            <span
              style={{
                ...styles.legendDot,
                background: stateColor(state),
              }}
            />
            <span>{state}</span>
          </div>
        ))}
        {allStates.length > topStates.length && (
          <div style={{ ...styles.legendItem, color: '#9ca3af' }}>
            +{allStates.length - topStates.length} more
          </div>
        )}
      </div>
      <div style={styles.container} ref={containerRef}>
        <svg ref={svgRef} />
      </div>
      {tooltip && (
        <div
          style={{
            ...styles.tooltip,
            left: tooltip.x,
            top: tooltip.y,
          }}
        >
          <div style={{ fontWeight: 600, color: '#1f2937', marginBottom: '4px' }}>
            {tooltip.item.label}
          </div>
          <div style={{ fontSize: '11px', color: '#9ca3af', marginBottom: '6px' }}>
            {tooltip.item.state}
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between', gap: '16px', marginBottom: '2px' }}>
            <span style={{ color: '#6b7280' }}>PC1</span>
            <span style={{ fontWeight: 600 }}>{tooltip.item.pc1.toFixed(3)}</span>
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between', gap: '16px' }}>
            <span style={{ color: '#6b7280' }}>PC2</span>
            <span style={{ fontWeight: 600 }}>{tooltip.item.pc2.toFixed(3)}</span>
          </div>
        </div>
      )}
    </div>
  );
}
